import { FormEvent, useContext, useEffect, useState } from "react";

import { useSearchParams } from "next/navigation";

import { Button, Form, Input } from "antd";
import useTranslation from "next-translate/useTranslation";

import useColectionsContext from "../../../../../modules/Collection/hooks/useColectionsContext";

import CircularProgress from "shared/components/atoms/CircularProgress";
import { FORM_ITEM_LAYOUT } from "shared/config";
import GlobalContext from "shared/providers/context/GlobalContext";

import Collections from "./components/Collections";
import { ContentButtons, ContentForm, Modal, Search } from "./styles";

const { TextArea } = Input;
const FormItem = Form.Item;

type ModalCollectionProps = {
  handleVisible: () => void;
  visible: boolean;
};

const ModalCollection = ({ handleVisible, visible }: ModalCollectionProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const params = useSearchParams();
  const id = params.get("id");
  const slug = params.get("slug");

  const { state } = useContext(GlobalContext);
  const {
    loader,
    entityType,
    collections,
    createCollection,
    getAllCollections,
    setItemCollections,
    getSingleCollection,
  } = useColectionsContext();

  const [filter, setFilter] = useState("");
  const [classItem, setClassItem] = useState("");
  const [titleIsEmpty, setTitleIsEmpty] = useState(false);
  const [visibleForm, setVisibleForm] = useState(false);
  const [newCollection, setNewCollection] = useState<any>({});
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const collectionType = entityType;

  const mode = state.theme.value;

  const collectionsList = collections;
  const collectionItems = [];

  collectionsList?.map((item) => {
    item?.resources.map((resource) => {
      collectionItems.push({
        id: resource.relation.id,
        class: resource.relation.className,
      });

      return collectionItems;
    });
    return null;
  });

  useEffect(() => {
    switch (collectionType?.entityType) {
      case "lesson":
        setClassItem("Vico068\\CourseTools\\Lesson");
        break;

      case "media":
        setClassItem("App\\MediaSpaceContent");
        break;

      case "course":
        setClassItem("Vico068\\CourseTools\\Course");
        break;

      case "blog":
        setClassItem("App\\Blog");
        break;

      case "community":
        setClassItem(" Deskti\\Forum\\Models\\ForumTopic");
        break;

      default:
        break;
    }
  }, [collectionType?.entityType]);

  const onFinish = (values: { name: string; description: string }) => {
    const { name, description } = values;

    if (/^\s*$/.test(name)) {
      setTitleIsEmpty(true);
      return;
    }

    setVisibleForm(false);
    createCollection({ name, description });
    setNewCollection({ name, description });
  };

  useEffect(() => {
    const newCollName = newCollection.name || null;
    const newCollDescription = newCollection.description || null;

    if (newCollName || newCollDescription) {
      const collections = collectionsList.filter(
        (collection) =>
          collection.name === newCollName &&
          collection.description === newCollDescription,
      );

      if (collections.length > 0) {
        collections.forEach((collection) => {
          setItemCollections({
            className: classItem,
            item: collectionType.id,
            collections: collection.id,
          });
        });
      }
    }
  }, [collectionsList, newCollection, collectionType, classItem]);

  return (
    <>
      <Modal
        title={t("common:addThisItemToCollection")}
        visible={visible}
        maskClosable={false}
        footer={null}
        onCancel={(e) => {
          e.stopPropagation();
          e.preventDefault();

          handleVisible();
          getAllCollections();

          if (id) getSingleCollection(id, slug);
        }}
      >
        {!visibleForm && (
          <>
            <Search
              enterButton
              disabled={loader}
              placeholder={t("common:searchCollections")}
              onChange={({ target: { value } }) => setFilter(value)}
            />

            {loader ? (
              <CircularProgress />
            ) : (
              <Collections filter={filter} classItem={classItem} />
            )}
          </>
        )}

        {visibleForm && (
          <Form
            form={form}
            onSubmitCapture={(event: FormEvent) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            layout="vertical"
            onFinish={onFinish}
            {...FORM_ITEM_LAYOUT}
          >
            <ContentForm>
              <FormItem
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("common:pleaseEnterCollectionName"),
                  },
                ]}
                validateStatus={titleIsEmpty && "error"}
                help={titleIsEmpty && t("common:pleaseEnterCollectionName")}
              >
                <Search
                  autoFocus
                  placeholder={t("common:name")}
                  onChange={({ target: { value } }) => {
                    setName(value);
                    setTitleIsEmpty(false);
                  }}
                />
              </FormItem>

              <FormItem name="description">
                <TextArea
                  rows={6}
                  placeholder={t("common:description")}
                  onChange={({ target: { value } }) => setDescription(value)}
                />
              </FormItem>
            </ContentForm>
          </Form>
        )}

        <ContentButtons>
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              setVisibleForm(!visibleForm);
            }}
          >
            {visibleForm ? (
              <>{t("common:cancel")}</>
            ) : (
              <>{t("common:createNewCollection")}</>
            )}
          </Button>

          {visibleForm && (
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                onFinish({ name, description });
              }}
              type="primary"
            >
              {t("common:createCollection")}
            </Button>
          )}

          {!visibleForm && (
            <Button
              type="primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                handleVisible();
                getAllCollections();

                if (id) getSingleCollection(id, slug);
              }}
            >
              {t("common:done")}
            </Button>
          )}
        </ContentButtons>
      </Modal>
    </>
  );
};

export default ModalCollection;
